// (B) Plain(플레인) Tabs 백엔드 — antd Tabs(rc-tabs) 대체. antd Tabs v5 DOM 이중클래스 미러링:
//   <div class="om-tabs om-tabs-<pos> om-tabs [om-tabs-card om-tabs-editable om-tabs-editable-card] [om-tabs-centered]">
//     <div.om-tabs-nav role=tablist> [<div.om-tabs-extra-content>{left}</div>] <div.om-tabs-nav-wrap> <div.om-tabs-nav-list>
//       {tab: <div.om-tabs-tab(+-active/-with-remove/-disabled)> <div.om-tabs-tab-btn role=tab>[icon]label</div>
//             [<button.om-tabs-tab-remove>×]}
//       [<button.om-tabs-nav-add>+]  [line: <div.om-tabs-ink-bar.om-tabs-ink-bar-animated style=left/width>]
//     <div.om-tabs-content-holder> <div.om-tabs-content.om-tabs-content-<pos>>
//       {<div.om-tabs-tabpane(+-active) role=tabpanel>{children}}
//   → 소비처 `.om-tabs*` SCSS(13파일) 무변경 매칭(ninegqlUI 미사용·JS 쿼리 0). 시각 base 는 styles/omTabs.css.
import {
  isValidElement,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import type { ReactNode } from 'react'
import type { OmwTabBarExtraContent, OmwTabsProps } from '../api/OmwTabs'
import { cx } from './cx'
import { useRovingKeydown } from './a11y/useRovingKeydown'
import '../styles/omwTabs.css'

// antd tabBarExtraContent 파리티: 단일 노드는 right, { left, right } 는 각 위치로. (nav 안 .om-tabs-extra-content)
function parseExtraContent(extra: OmwTabBarExtraContent | undefined): {
  left: ReactNode
  right: ReactNode
} {
  if (extra == null) return { left: null, right: null }
  if (
    typeof extra === 'object' &&
    !isValidElement(extra) &&
    ('left' in extra || 'right' in extra)
  ) {
    const obj = extra as { left?: ReactNode; right?: ReactNode }
    return { left: obj.left ?? null, right: obj.right ?? null }
  }
  return { left: null, right: extra as ReactNode }
}

export function PlainTabs({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  centered = false,
  destroyOnHidden = false,
  type = 'line',
  tabPosition = 'top',
  hideAdd = false,
  onEdit,
  onTabClick,
  tabBarExtraContent,
  className,
  style,
  'aria-label': ariaLabel,
}: OmwTabsProps) {
  const { left: extraLeft, right: extraRight } = parseExtraContent(tabBarExtraContent)
  const isControlled = activeKey !== undefined
  const firstKey = items[0]?.key ?? ''
  const [innerKey, setInnerKey] = useState(defaultActiveKey ?? firstKey)
  const current = isControlled ? (activeKey ?? '') : innerKey
  const editable = type === 'editable-card'

  // 방문한 탭만 마운트(antd lazy) — 방문 후 유지(destroyOnHidden 시 비활성 언마운트)
  const [visited, setVisited] = useState<Set<string>>(() => new Set([current]))

  // current(활성 탭)는 클릭(selectKey) 외에 controlled activeKey 변경으로도 바뀐다.
  // 프로그램적으로 활성화된 탭(예: host 가 새 편집 탭을 열며 activeKey 를 바꾸는 경우)도
  // 방문 처리해, 이후 다른 탭으로 전환할 때 언마운트/재마운트(= 편집기 내용 리셋)되지 않고
  // keep-alive 되도록 한다. destroyOnHidden 소비처는 mounted 공식의 `!destroyOnHidden` 로
  // 걸러지므로 영향 없음.
  useLayoutEffect(() => {
    setVisited((prev) => (prev.has(current) ? prev : new Set(prev).add(current)))
  }, [current])

  const navListRef = useRef<HTMLDivElement>(null)
  const [ink, setInk] = useState<{ left: number; width: number }>({ left: 0, width: 0 })

  const selectKey = (key: string) => {
    if (!isControlled) setInnerKey(key)
    setVisited((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
    onChange?.(key)
  }

  // line 타입 ink-bar 위치 측정(활성 탭 offset)
  const horizontal = tabPosition === 'top' || tabPosition === 'bottom'
  useLayoutEffect(() => {
    if (type !== 'line') return
    const list = navListRef.current
    if (!list) return
    const activeTab = list.querySelector<HTMLElement>('.om-tabs-tab-active')
    if (!activeTab) return
    setInk(
      horizontal
        ? { left: activeTab.offsetLeft, width: activeTab.offsetWidth }
        : { left: activeTab.offsetTop, width: activeTab.offsetHeight },
    )
  }, [current, items, type, horizontal, tabPosition])

  const inkStyle: CSSProperties = horizontal
    ? { left: ink.left, width: ink.width }
    : { top: ink.left, height: ink.width }

  // ── tablist 키보드 네비게이션(WAI-ARIA, 수동 활성화) ──────────────────
  // 화살표=포커스 이동(roving), Home/End, Enter/Space=활성. antd rc-tabs 화살표 회귀 복원.
  const tabBtnRefs = useRef(new Map<string, HTMLDivElement>())
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const focusIndex = items.findIndex((it) => it.key === focusKey)
  const selectedIndex = items.findIndex((it) => it.key === current)
  const lastTabKeyEvent = useRef<ReactKeyboardEvent<HTMLDivElement> | null>(null)

  const { onKeyDown: rovingKeyDown } = useRovingKeydown({
    itemCount: items.length,
    activeIndex: focusIndex >= 0 ? focusIndex : selectedIndex,
    setActiveIndex: (i) => {
      const it = items[i]
      if (it == null) return
      setFocusKey(it.key)
      tabBtnRefs.current.get(it.key)?.focus()
    },
    onActivate: (i) => {
      const it = items[i]
      if (it == null || it.disabled) return
      const e = lastTabKeyEvent.current
      if (e) onTabClick?.(it.key, e)
      selectKey(it.key)
    },
    isDisabled: (i) => !!items[i]?.disabled,
    getLabel: (i) => {
      const label = items[i]?.label
      return typeof label === 'string' ? label : ''
    },
    orientation: horizontal ? 'horizontal' : 'vertical',
  })

  const onTablistKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    lastTabKeyEvent.current = e
    rovingKeyDown(e)
  }

  return (
    <div
      className={cx(
        'om-tabs',
        `om-tabs-${tabPosition}`,
        'om-tabs',
        editable && 'om-tabs-card om-tabs-editable om-tabs-editable-card',
        centered && 'om-tabs-centered',
        className,
      )}
      style={style}
      aria-label={ariaLabel}
    >
      <div
        className="om-tabs-nav"
        role="tablist"
        aria-orientation={horizontal ? 'horizontal' : 'vertical'}
        onKeyDown={onTablistKeyDown}
      >
        {extraLeft != null && <div className="om-tabs-extra-content">{extraLeft}</div>}
        <div className="om-tabs-nav-wrap">
          <div className="om-tabs-nav-list" ref={navListRef}>
            {items.map((item) => {
              const active = item.key === current
              const removable = editable && item.closable !== false && !item.disabled
              return (
                <div
                  key={item.key}
                  className={cx(
                    'om-tabs-tab',
                    active && 'om-tabs-tab-active',
                    removable && 'om-tabs-tab-with-remove',
                    item.disabled && 'om-tabs-tab-disabled',
                  )}
                  onClick={(e) => {
                    if (item.disabled) return
                    // antd 파리티: onTabClick 은 활성 변경 여부와 무관하게 매 클릭 호출(onChange 이전).
                    // 탭 셀(.om-tabs-tab) 전체를 클릭 영역으로 둔다(글자 영역 .om-tabs-tab-btn 한정 X).
                    onTabClick?.(item.key, e)
                    selectKey(item.key)
                  }}
                >
                  <div
                    ref={(el) => {
                      if (el) tabBtnRefs.current.set(item.key, el)
                      else tabBtnRefs.current.delete(item.key)
                    }}
                    role="tab"
                    aria-selected={active}
                    aria-disabled={item.disabled || undefined}
                    tabIndex={item.disabled ? undefined : active ? 0 : -1}
                    className="om-tabs-tab-btn"
                    onFocus={() => setFocusKey(item.key)}
                  >
                    {item.icon && <span className="om-tabs-tab-icon">{item.icon}</span>}
                    {item.label}
                  </div>
                  {removable && (
                    <button
                      type="button"
                      className="om-tabs-tab-remove"
                      aria-label="remove"
                      tabIndex={active ? 0 : -1}
                      onClick={(e) => {
                        e.stopPropagation()
                        onEdit?.(item.key, 'remove')
                      }}
                    >
                      {item.closeIcon ?? '×'}
                    </button>
                  )}
                </div>
              )
            })}
            {editable && !hideAdd && (
              <button
                type="button"
                className="om-tabs-nav-add"
                aria-label="Add tab"
                onClick={() => onEdit?.('', 'add')}
              >
                +
              </button>
            )}
            {type === 'line' && (
              <div className="om-tabs-ink-bar om-tabs-ink-bar-animated" style={inkStyle} />
            )}
          </div>
        </div>
        {extraRight != null && <div className="om-tabs-extra-content">{extraRight}</div>}
      </div>
      <div className="om-tabs-content-holder">
        <div className={cx('om-tabs-content', `om-tabs-content-${tabPosition}`)}>
          {items.map((item) => {
            const active = item.key === current
            // forceRender: 미방문이어도 미리 마운트(antd 파리티). destroyOnHidden 보다 우선(항상 마운트 유지).
            const mounted =
              active || item.forceRender || (visited.has(item.key) && !destroyOnHidden)
            return (
              <div
                key={item.key}
                role="tabpanel"
                aria-hidden={!active}
                hidden={!active}
                tabIndex={active ? 0 : -1}
                className={cx('om-tabs-tabpane', active && 'om-tabs-tabpane-active')}
              >
                {mounted ? item.children : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
